import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { Trash2, Layers, FolderTree, Download, Upload } from 'lucide-react'
import { ImportStandardModal } from '@/components/admin/ImportStandardModal'


interface StandardItem {
  id: number
  name: string
  sort_order: number
  chapters_count: number
}

interface Props {
  standards: StandardItem[]
}

export default function StandardsIndex({ standards }: Props) {
  const [name, setName] = useState('')
  const [sortOrder, setSortOrder] = useState<number | ''>(0)
  const [importModalOpen, setImportModalOpen] = useState(false)

  const handleCreate = (e: React.FormEvent) => {

    e.preventDefault()
    if (!name.trim()) return

    router.post('/admin/standards', {
      standard: {
        name: name.trim(),
        sort_order: sortOrder === '' ? 0 : sortOrder
      }
    }, {
      onSuccess: () => {
        setName('')
        setSortOrder(0)
      }
    })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure you want to delete this standard?')) return
    router.delete(`/admin/standards/${id}`)
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold">Taxonomy Management</h1>
          <p className="text-muted-foreground mt-1">Manage standards, curriculum, subjects, and exams</p>
        </div>

        {/* Create form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Add Standard</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5 flex-1 min-w-[200px]">
                <label className="text-sm font-medium">Standard Name *</label>
                <Input
                  placeholder="e.g. XI, XII, 10th, Dropper"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5 w-32">
                <label className="text-sm font-medium">Sort Order</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value ? Number(e.target.value) : '')}
                />
              </div>

              <Button type="submit" disabled={!name.trim()}>
                Add Standard
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Standards ({standards.length})</CardTitle>
            <div className="flex items-center gap-2">
              {standards.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 font-medium text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete ALL ${standards.length} standards? This will unlink them from chapters, syllabi, and questions.`)) {
                      router.delete('/admin/standards/destroy_all')
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 font-medium"
                onClick={() => setImportModalOpen(true)}
              >
                <Upload className="h-4 w-4 text-muted-foreground" />
                Import JSON
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 font-medium" asChild>
                <a href="/admin/standards/export">
                  <Download className="h-4 w-4 text-muted-foreground" />
                  Export JSON
                </a>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Standard Name</TableHead>
                  <TableHead className="w-32">Sort Order</TableHead>
                  <TableHead className="w-44">Associated Chapters</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {standards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      No standards created yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  standards.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-semibold">
                        <Link
                          href={`/admin/standards/${s.id}`}
                          className="hover:underline flex items-center gap-1.5 text-primary"
                        >
                          <Layers className="h-4 w-4 text-muted-foreground" />
                          {s.name}
                        </Link>
                      </TableCell>
                      <TableCell>{s.sort_order}</TableCell>
                      <TableCell>
                        <span className="font-medium">{s.chapters_count}</span> chapters
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          <Button size="icon" variant="ghost" asChild title="Manage Standard Curriculum">
                            <Link href={`/admin/standards/${s.id}`}>
                              <FolderTree className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(s.id)}
                            title="Delete Standard"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Import Modal */}
        <ImportStandardModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
        />
      </div>
    </AppShell>
  )
}

